/**
 * Large-batch upload reliability primitives (#GR9g). A 296-image batch dropped ~149
 * SILENTLY — un-retried transient failures (network blips / Cloudflare Images 429s /
 * 5xx) and, worst case, one item's throw halting the whole loop with zero signal.
 * These three primitives make the uploader reliable AND honest:
 *   - withRetry: retry a transient step a few times with backoff before it's declared failed
 *   - runPool: bounded concurrency (never fire 296 at once) where ONE bad item can't
 *     halt the rest (the silent-loss guard)
 *   - isTransient: which failures are worth retrying (network / 429 / 5xx) vs permanent
 * All deterministic, no AI. Pure functions → unit-tested in isolation.
 */

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Permanent client errors — retrying won't help (except 429, which IS transient). */
const PERMANENT = /\b(400|401|403|404|409|422)\b/;

/** A failure worth retrying: a network error (fetch throws TypeError), a 429/5xx, or an
 *  unclassified error (default transient — the retry cap bounds any mistake). */
export function isTransient(e: unknown): boolean {
  if (e instanceof TypeError) return true; // fetch network failure
  const msg = e instanceof Error ? e.message : String(e);
  if (/\b429\b/.test(msg)) return true; // rate-limit — always retry
  return !PERMANENT.test(msg);
}

export interface RetryOptions {
  retries?: number; // additional attempts after the first (default 3 → 4 tries total)
  baseMs?: number; // backoff base (default 500)
  maxMs?: number; // backoff cap (default 8000)
  isRetryable?: (e: unknown) => boolean;
  sleep?: (ms: number) => Promise<void>;
  rand?: () => number; // jitter source (injectable for tests)
}

/** Run `fn`, retrying transient failures with exponential backoff + jitter. Throws the
 *  last error once retries are exhausted or the error isn't retryable. */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const retries = opts.retries ?? 3;
  const base = opts.baseMs ?? 500;
  const max = opts.maxMs ?? 8_000;
  const retryable = opts.isRetryable ?? isTransient;
  const sleep = opts.sleep ?? realSleep;
  const rand = opts.rand ?? Math.random;
  let attempt = 0;
  for (;;) {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= retries || !retryable(e)) throw e;
      const backoff = Math.min(max, base * 2 ** attempt);
      await sleep(backoff + backoff * 0.25 * rand()); // full backoff + up to 25% jitter
      attempt += 1;
    }
  }
}

/**
 * Process `items` through `worker` with BOUNDED concurrency. Crucially, a worker that
 * throws is CONTAINED — it never halts the pool, so one bad item can't silently drop
 * the rest (the #GR9g root cause). The worker is expected to record each item's outcome
 * itself (it should not throw); the try/catch here is defence-in-depth.
 */
export async function runPool<T>(
  items: T[],
  worker: (item: T, index: number) => Promise<void>,
  concurrency: number,
): Promise<void> {
  let next = 0;
  const runners = Math.max(1, Math.min(concurrency, items.length || 1));
  const runner = async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      try {
        await worker(items[i], i);
      } catch {
        /* a worker should handle its own errors; this guard stops one item halting the pool */
      }
    }
  };
  await Promise.all(Array.from({ length: runners }, runner));
}
